import axios, {
  AxiosInstance,
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig as BaseInternalAxiosRequestConfig
} from 'axios'
import { type AxiosHeaders } from 'axios'
import { showFullScreenLoading, tryHideFullScreenLoading } from '@/utils/fullScreenLoading'
import { AxiosCanceler } from './helper/axiosCancel'
import { ResultData } from '@/api/interface/index'
import { ResultEnum } from '@/enum/httpEnum'
import { checkStatus } from './helper/checkStatus'
import toast from '@/components/Toast/type/index.module'
import { GlobalStore } from '@/store'
import router from '@/routers'
import { showServerUpdating } from '@/utils/serverUpdating/index'
import { emojiDecode, getEncryptConfig } from './helper/encrypt'
import '@/auth/jwt'
import { getAccessToken, clearAuthTokens, setAuthTokens } from '@/auth/jwt'

/**请求是否需要加密 */
const requestNeedEncrypt = import.meta.env.VITE_REQUEST_NEED_ENCRYPT

/**
 * pinia 错误使用说明示例
 * https://github.com/vuejs/pinia/discussions/971
 * https://github.com/vuejs/pinia/discussions/664#discussioncomment-1329898
 * https://pinia.vuejs.org/core-concepts/outside-component-usage.html#single-page-applications
 */
// const globalStore = GlobalStore();

const axiosCanceler = new AxiosCanceler()
let serverUpdating: undefined | null
//额外增加请求的配置内容，防止head被拦截
export interface CustomRequestConfig extends AxiosRequestConfig<object> {
  /**是否触发全屏加载，默认true */
  showLoading?: boolean
  /**是否跳过取消器检查。用于可能同时触发多次的接口，例如图片批量上传。默认false*/
  ignoreCanceler?: boolean
  /** 自定义超时时间，用于覆盖默认超时时间 */
  customTimeout?: number
}

// Extend InternalAxiosRequestConfig to include custom properties
interface InternalAxiosRequestConfig<T> extends BaseInternalAxiosRequestConfig<T> {
  // @ts-ignore
  [x: string]: any
  customTimeout?: number
}

// const platformCode = import.meta.env.META_CODE;

const config = {
  // 默认地址请求地址，可在 .env 开头文件中修改

  baseURL: import.meta.env.VITE_API_URL as string,
  // 设置超时时间（10s）
  timeout: ResultEnum.TIMEOUT as number,
  // 跨域时候允许携带凭证
  withCredentials: false
}

class RequestHttp {
  service: AxiosInstance
  public constructor(config: AxiosRequestConfig) {
    // 实例化axios
    this.service = axios.create(config)
    /**
     * @description 请求拦截器
     * 客户端发送请求 -> [请求拦截器] -> 服务器
     * token校验(JWT) : 接受服务器返回的token,存储到vuex/pinia/本地储存当中
     */
    this.service.interceptors.request.use(
      // @ts-ignore
      async (config: InternalAxiosRequestConfig) => {
        // 使用Token绕过标头校验
        ;(config.headers as AxiosHeaders).set('Token', import.meta.env.VITE_META_CODE)
        const globalStore = GlobalStore()
        // 修改超时时间
        // @ts-ignore
        config.customTimeout && (config.timeout = config.customTimeout)
        // * 将当前请求添加到 pending 中
        // @ts-ignore
        !config.ignoreCanceler && axiosCanceler.addPending(config)
        // * 如果当前请求不需要显示 loading,在 api 服务中通过指定的第三个参数: { headers: { noLoading: true } }来控制不显示loading，参见loginApi
        // @ts-ignore
        config.showLoading !== false && showFullScreenLoading()

        /**是否需要请求加密 */
        if (requestNeedEncrypt === 'yes') {
          // 加密模式下，打印请求信息以供调试
          console.log(
            '【加密请求信息】',
            config.method,
            '\nurl：',
            config.url,
            '\nquery参数：',
            config.params,
            '\nparam参数：',
            config.data
          )
          config = getEncryptConfig(config)
        }

        // 使用 axios-jwt 读取 token 并设置 Authorization 头
        const accessToken = await getAccessToken()
        if (accessToken) {
          ;(config.headers as AxiosHeaders).set('Authorization', `Bearer ${accessToken}`)
        }

        return config
      },
      (error: AxiosError) => {
        return Promise.reject(error)
      }
    )

    /**
     * @description 响应拦截器
     *  服务器换返回信息 -> [拦截统一处理] -> 客户端JS获取到信息
     */
    this.service.interceptors.response.use(
      async (response: AxiosResponse) => {
        const { data, config } = response

        // 将emoji编码 转回为emoji。本方法无视是否加密
        if (data?.data) {
          data.data = JSON.parse(emojiDecode(JSON.stringify(data.data)))
        }

        const globalStore = GlobalStore()
        // * 在请求结束后，移除本次请求，并关闭请求 loading
        axiosCanceler.removePending(config)
        tryHideFullScreenLoading()
        // for test
        // if (index > 20 && index < 30) {
        // 	data.code = 999;
        // }
        // * 维护中（code == 999）
        if (data.code === ResultEnum.SERVER_UPDATING) {
          // @ts-ignore
          !serverUpdating && (serverUpdating = showServerUpdating())
          return Promise.reject(data)
        }
        if (serverUpdating) {
          serverUpdating = null
        }
        // * 登陆失效,使用游客登录
        // if (data.code === ResultEnum.OVERDUE) {
        // 	const visitoreToken = await login();
        // 	globalStore.setToken(visitoreToken);
        // 	try {
        // 		delete config.cancelToken;
        // 		config.headers!.Authorization = visitoreToken;
        // 		let res = await axios.create(config)(config);
        // 		if (res.data) return res.data;
        // 		return Promise.reject(res);
        // 	} catch (e) {
        // 		console.error(e);
        // 		return Promise.reject(e);
        // 	}
        // }
        if (
          [
            ResultEnum.CORP_DISABLED,
            ResultEnum.PLATFORM_DISABLED,
            ResultEnum.USER_DISABLED,
            ResultEnum.USER_LEFT
          ].includes(data.code)
        ) {
          toast.error(data.message)
          await clearAuthTokens()
          globalStore.setToken('')
          router.replace({
            path: '/home/index'
          })
          return Promise.reject(data)
        }
        // token过期，无感刷新并重发请求
        if (data.code === ResultEnum.REFRESH) {
          if (data.data) {
            await setAuthTokens({ accessToken: data.data, refreshToken: data.data })
            globalStore.setToken(data.data)
            try {
              delete config.cancelToken
              config.headers!.Authorization = data.data
              const res = await axios.create(config)(config)
              if (res.data) return res.data

              return Promise.reject(res)
            } catch (e) {
              console.error(e)
              return Promise.reject(e)
            }
          }
        }
        // * 全局错误信息拦截（防止下载文件得时候返回数据流，没有code，直接报错）
        if (data.code && data.code !== ResultEnum.SUCCESS) {
          toast.error(data.message)
          return Promise.reject(data)
        }
        // 加密模式下，打印请求返回以供调试
        if (requestNeedEncrypt === 'yes') {
          console.log('【请求返回】', '\nurl：', config.url, '\n返回内容：', data)
        }

        // * 成功请求（在页面上除非特殊情况，否则不用处理失败逻辑）
        return data
      },
      async (error: AxiosError) => {
        const { response } = error
        tryHideFullScreenLoading()
        // 请求超时单独判断，因为请求超时没有 response
        if (error.message.indexOf('timeout') !== -1) toast.error('请求超时！请您稍后重试')
        // 根据响应的错误状态码，做不同的处理
        if (response) checkStatus(response.status)
        // 服务器结果都没有返回(可能服务器错误可能客户端断网)，断网处理:可以跳转到断网页面
        if (!window.navigator.onLine) router.replace({ path: '/500' })
        return Promise.reject(error)
      }
    )
  }

  // * 常用请求方法封装
  get<T>(url: string, params?: object, _object = {}): Promise<ResultData<T>> {
    return this.service.get(url, { params, ..._object })
  }
  post<T>(url: string, params?: object, _object = {}): Promise<ResultData<T>> {
    return this.service.post(url, params, _object)
  }
  put<T>(url: string, params?: object, _object = {}): Promise<ResultData<T>> {
    return this.service.put(url, params, _object)
  }
  delete<T>(url: string, params?: any, _object = {}): Promise<ResultData<T>> {
    return this.service.delete(url, { params, ..._object })
  }
}

export default new RequestHttp(config)
